import { Heading, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { HeroVideo, HeroContent } from '@/entities/product';
import { headerHeight } from '@/shared/constants';
import { DefaultContainer, VideoBlock } from '@/shared/ui';

const MIN_HEADING = `calc(100vh - ${headerHeight})`;

interface HeroBlockProps extends Partial<HeroVideo> {
  heroContent?: Partial<HeroContent>;
  productType?: 'card' | 'stand' | 'plate';
}

const HeroBlock = ({ src, srcFallback, heroContent, productType }: HeroBlockProps) => {
  const { t } = useTranslation();

  // Determine the correct description translation key based on product type
  const getDescriptionKey = () => {
    if (!productType) return 'store.productPage.hero.description';
    return `store.productPage.hero.${productType}Description`;
  };

  const mainHeading = heroContent?.mainHeading || t('store.productPage.hero.mainHeading');
  const subHeading = heroContent?.subHeading || t('store.productPage.hero.subHeading');
  const description = heroContent?.description || t(getDescriptionKey());
  return (
    <DefaultContainer
      alignItems='center'
      bg='bg.light'
      containerProps={{ position: 'relative' }}
      flexDirection={{ base: 'column', md: 'row' }}
      gap='lg'
      minH={MIN_HEADING}
    >
      {src && (
        <VideoBlock
          h={{ base: MIN_HEADING, md: 'full' }}
          left={0}
          objectFit='fill'
          overflow='hidden'
          position='absolute'
          src={src}
          srcFallback={srcFallback}
          top={0}
          type='video/webm'
          typeFallback='video/mp4'
          w='full'
        />
      )}
      <VStack
        align={{ base: 'center', md: 'start' }}
        bgColor={{ base: 'transparent', md: 'whiteAlpha.900' }}
        borderRadius='xl'
        maxW={{ base: 'full', md: '35rem' }}
        mt={{ base: MIN_HEADING, md: 0 }}
        px={{ base: 0, md: 'xl' }}
        py='xl'
        spacing='md'
        textAlign={{ base: 'center', md: 'left' }}
        zIndex={1}
      >
        <Heading
          as='h1'
          size={{ base: '2xl', lg: '3xl' }}
        >
          {mainHeading}
        </Heading>
        <Heading
          as='h2'
          color='primary.pure'
          size={{ base: 'md', lg: 'lg' }}
          whiteSpace='pre-wrap'
        >
          {subHeading}
        </Heading>
        <Text
          fontSize={{ base: 'lg', lg: 'xl' }}
          fontWeight='semibold'
        >
          {description}
        </Text>
      </VStack>
    </DefaultContainer>
  );
};

export default HeroBlock;
